/**
 * dashboard.* widgets — wrap each fixed dashboard section as an
 * AI-composable widget kind. Together with compose_panel(page_kind:
 * "dashboard"), these let the Operative completely redesign the
 * dashboard layout: reorder, hide, replace, or interleave sections
 * with new AI-composed primitives (heroes, stat_cards, charts, decks).
 *
 * Each widget here is the same component the legacy DashboardPage
 * rendered inline; pulled out so the AI can reference it by widget_id
 * inside a PanelSpec.
 *
 * DashboardStats is the exception: it is the fixed workspace summary
 * row DashboardPage renders above the default layout (it is not a
 * registered widget, so an AI composition fully replaces the page).
 *
 * Registration: src/app/widgets/registry.tsx maps the IDs.
 */

import { useQuery } from "@tanstack/react-query";
import { DataUnavailable } from "../../../framework/react/DataUnavailable.js";
import { Link } from "react-router-dom";
import {
  Activity,
  ArrowRight,
  BarChart3,
  CircleCheck,
  FileText,
  Inbox,
  ListChecks,
  Sparkles,
  Tags,
  Users,
  Vote,
} from "lucide-react";
import type { WidgetProps } from "../registry";
import {
  DashboardFeed,
  WorkspaceActivityStream,
} from "../../../framework/react/index.js";
import { WidgetGrid } from "../../components/WidgetGrid";
import {
  Badge,
  Card,
  EmptyState,
  SectionHeading,
  Skeleton,
  SkeletonRows,
  StatCard,
  buttonClass,
  type BadgeTone,
} from "../../components/ui";
import { getCsrfHeader } from "../../lib/csrf";
import { legislationRequest } from "../../lib/legislation";
import type { NotificationInboxResult } from "../../lib/notifications";
import {
  humanizePolicyInboxSource,
  policyInboxMediatorLabel,
  usePolicyInboxScans,
  type PolicyInboxScan,
} from "../../hooks/usePolicyInboxScans";
import {
  usePolicyInboxWorkPlan,
  type PolicyInboxWorkPlanSummary,
} from "../../hooks/usePolicyInboxWorkPlan";
import {
  dashboardPolicyIntelligenceIntakeHref,
  dashboardPolicyIntelligenceLensKey,
  dashboardPolicyIntelligenceSearchParams,
  describeDashboardPolicyIntelligenceLens,
  normalizeDashboardPolicyIntelligenceLens,
} from "../../../lib/dashboard-policy-intelligence-lens.js";

// ── Shared kickoff fetch — react-query dedupes across widgets ─────────

interface KickoffPayload {
  user: { id: string; email: string | null; name: string | null; tier: string | null };
  workspaces: Array<{
    id: string;
    name: string;
    type: string;
    current_focus: string | null;
    tracked_bills: number;
    pending_homework: number;
    pending_homework_assigned_to_you: number;
    recent_actions_7d: number;
  }>;
  top_attention: Array<{
    id: string;
    workspace_id: string;
    workspace_name: string | null;
    summary: string | null;
    why_user: string | null;
    is_assigned_to_you: boolean;
  }>;
  starter_suggestions: string[];
  narration: string;
}

function useKickoff() {
  return useQuery({
    queryKey: ["session-kickoff"],
    queryFn: async () => {
      const res = await fetch("/api/v1/session-kickoff", { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch session kickoff");
      return (await res.json()) as KickoffPayload;
    },
  });
}

/** Small "View all" style link for a card header. */
function CardLink({ to, children }: { to: string; children: React.ReactNode }) {
  return (
    <Link to={to} className="inline-flex items-center gap-1 text-xs font-semibold text-action hover:underline">
      {children}
      <ArrowRight size={12} aria-hidden="true" />
    </Link>
  );
}

/** Party chip colors (semantic: D / R / independent). */
function partyChipClass(party: string | null | undefined): string {
  const p = (party || "?").charAt(0);
  if (p === "R") return "bg-red-100 text-red-800";
  if (p === "D") return "bg-sky-100 text-sky-800";
  return "bg-purple-100 text-purple-800";
}

const LIST_ROW = "block rounded-md border border-transparent px-3 py-2 text-sm transition-colors hover:border-stone-200 hover:bg-stone-50";

// ── Workspace stats row (fixed chrome above the default layout) ───────
//
// Every count comes from an endpoint another page already reads, with
// the same query key and response shape so react-query shares the cache
// (Bills, Inbox badge, Whip boards, Issues).

function statValue(value: number | null | undefined, loading: boolean) {
  if (loading) return <Skeleton className="h-6 w-10" />;
  return value == null ? "—" : value.toLocaleString();
}

export function DashboardStats({ workspaceId }: { workspaceId: string }) {
  const kickoff = useKickoff();
  const workspace = kickoff.data?.workspaces.find((w) => w.id === workspaceId) ?? null;
  const notifications = useQuery({
    queryKey: ["notifications", workspaceId, "badge"],
    queryFn: ({ signal }) => legislationRequest<NotificationInboxResult>(`/api/v1/workspaces/${encodeURIComponent(workspaceId)}/notifications?unread_only=true&limit=1`, { signal }),
  });
  const whipBoards = useQuery({
    queryKey: ["whip-boards", workspaceId, "all"],
    queryFn: async () => {
      const res = await fetch(`/api/v1/workspaces/${workspaceId}/whip-boards?scope=all`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load whip boards");
      return res.json() as Promise<{ boards: Array<{ id: string }> }>;
    },
  });
  const issues = useQuery({
    queryKey: ["issues-index", workspaceId],
    queryFn: async () => {
      const res = await fetch(`/api/v1/workspaces/${workspaceId}/issues-index`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load issues");
      return res.json();
    },
  });
  const unread = notifications.data ? (notifications.data.unread_count_total ?? notifications.data.unread_count ?? 0) : null;
  const issueCount = Array.isArray(issues.data?.issues)
    ? (issues.data.issues as Array<{ archived_at?: string | null }>).filter((i) => !i.archived_at).length
    : null;
  const assigned = workspace?.pending_homework_assigned_to_you ?? 0;
  if (kickoff.error || notifications.error || whipBoards.error || issues.error) {
    return <DataUnavailable message="Workspace summary could not be loaded." retry={() => Promise.all([
      kickoff.refetch(), notifications.refetch(), whipBoards.refetch(), issues.refetch(),
    ])} />;
  }
  return (
    <section aria-label="Workspace summary" className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-5">
      <StatCard
        icon={FileText}
        label="Tracked bills"
        value={statValue(workspace?.tracked_bills, kickoff.isLoading)}
        hint={workspace ? `${workspace.recent_actions_7d.toLocaleString()} actions in 7 days` : undefined}
        to="/bills"
      />
      <StatCard
        icon={Inbox}
        label="Unread alerts"
        value={statValue(unread, notifications.isLoading)}
        hint={notifications.error ? "Count unavailable" : "In your inbox"}
        to={`/notifications?workspace_id=${encodeURIComponent(workspaceId)}`}
      />
      <StatCard
        icon={ListChecks}
        label="Needs attention"
        value={statValue(workspace?.pending_homework, kickoff.isLoading)}
        hint={assigned > 0 ? `${assigned.toLocaleString()} assigned to you` : "None assigned to you"}
      />
      <StatCard
        icon={Vote}
        label="Whip boards"
        value={statValue(whipBoards.data?.boards.length, whipBoards.isLoading)}
        hint="Standalone, bill and issue boards"
        to="/whip-boards"
      />
      <StatCard
        icon={Tags}
        label="Issues"
        value={statValue(issueCount, issues.isLoading)}
        hint="Active policy issues"
        to="/issues"
      />
    </section>
  );
}

// ── dashboard.narration ───────────────────────────────────────────────

export function DashboardNarration(_props: WidgetProps) {
  const { data: kickoff, isLoading, isError, refetch } = useKickoff();
  const suggestions = kickoff?.starter_suggestions?.slice(0, 4) ?? [];
  return (
    <Card title={<span className="inline-flex items-center gap-1.5"><Sparkles size={14} aria-hidden="true" />Your briefing</span>} className="h-full">
      {isLoading ? (
        <div role="status" className="space-y-2">
          <span className="sr-only">Loading…</span>
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-4/5" />
          <Skeleton className="h-4 w-3/5" />
        </div>
      ) : isError ? (
        <DataUnavailable message="Your briefing could not be loaded." retry={refetch} />
      ) : (
        <p className="text-sm leading-relaxed text-stone-800">{kickoff?.narration ?? "No briefing available yet."}</p>
      )}
      {!isError && suggestions.length > 0 && (
        <div className="mt-4">
          <div className="mb-1.5 text-xs font-medium text-stone-500">Suggested next steps — ask your AI client</div>
          <ul className="space-y-1.5">
            {suggestions.map((s, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-stone-700">
                <ArrowRight size={14} className="mt-0.5 shrink-0 text-stone-400" aria-hidden="true" />
                <span>{s}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </Card>
  );
}

// ── dashboard.workspace_summary ───────────────────────────────────────

export function DashboardWorkspaceSummary({ workspaceId }: WidgetProps) {
  const { data: kickoff, error, refetch } = useKickoff();
  if (error) return <DataUnavailable message="Workspace summary could not be loaded." retry={refetch} />;
  const workspace = kickoff?.workspaces.find((w) => w.id === workspaceId) ?? null;
  if (!workspace) return null;
  return (
    <Card
      title={workspace.name}
      description={workspace.current_focus ? `Focus: ${workspace.current_focus}` : undefined}
      actions={<Badge tone="outline">{workspace.type}</Badge>}
    >
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <StatCard icon={FileText} label="Tracked bills" value={workspace.tracked_bills} />
        <StatCard icon={ListChecks} label="Pending homework" value={workspace.pending_homework} />
        <StatCard
          icon={Users}
          label="Assigned to you"
          value={<span className={workspace.pending_homework_assigned_to_you > 0 ? "text-amber-700" : undefined}>{workspace.pending_homework_assigned_to_you}</span>}
        />
        <StatCard icon={Activity} label="Activity (7d)" value={workspace.recent_actions_7d} />
      </div>
    </Card>
  );
}

// ── dashboard.top_attention ───────────────────────────────────────────

export function DashboardTopAttention(_props: WidgetProps) {
  const { data: kickoff, isLoading, isError, refetch } = useKickoff();
  const items = kickoff?.top_attention ?? [];
  return (
    <Card
      title="Top attention items"
      description="Homework and follow-ups your workspace flagged, most important first."
      actions={items.length > 0 ? <Badge tone="neutral">{items.length}</Badge> : undefined}
      className="h-full"
      bodyClassName={items.length > 0 ? "py-1" : undefined}
    >
      {isLoading ? (
        <SkeletonRows rows={3} label="Loading attention items…" />
      ) : isError ? (
        // A failed load is not an empty queue: never claim "nothing pending"
        // when the attention list could not be read.
        <DataUnavailable message="Attention items could not be loaded." retry={refetch} />
      ) : items.length === 0 ? (
        <EmptyState icon={<CircleCheck size={20} />} title="Nothing pending — your queue is clear.">
          New homework appears here when a tracked bill moves, a monitor fires or a teammate assigns you something.
          Ask your AI client “what should I work on next?” to plan ahead.
        </EmptyState>
      ) : (
        <ul className="divide-y divide-stone-100">
          {items.map((item) => (
            <li key={item.id} className="flex items-start gap-3 px-4 py-3">
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium text-stone-900">
                  {item.summary ?? `(${item.id.slice(0, 8)})`}
                </div>
                {item.why_user && <div className="mt-0.5 text-xs text-stone-600">{item.why_user}</div>}
                {item.workspace_name && <div className="mt-0.5 text-[11px] text-stone-500">{item.workspace_name}</div>}
              </div>
              {item.is_assigned_to_you && <Badge tone="warn" className="mt-0.5 shrink-0">Assigned to you</Badge>}
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

// ── dashboard.policy_intelligence ────────────────────────────────────

interface PolicyDashboardStory {
  id: string;
  title: string;
  summary: string;
  priority_tier: "urgent" | "high" | "normal" | "background";
  priority_score: number;
  source_count: number;
  needs_review?: boolean;
  reviewed_evidence_revision?: number;
  new_source_count?: number;
  last_seen_at: string;
}

function compactCoverageDate(value: string | null | undefined): string {
  if (!value) return "unknown time";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "unknown time";
  return new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
}

function compactScanCopy(scan: PolicyInboxScan): string {
  const source = humanizePolicyInboxSource(scan.source_key);
  const receipts = scan.receipt_summary;
  if (scan.status === "running") {
    return `${policyInboxMediatorLabel(scan.imported_via)} scan open for ${source} · last checkpoint ${compactCoverageDate(scan.last_activity_at)}`;
  }
  if (scan.status === "completed") {
    const empty = scan.messages_selected === 0 && receipts.selected_message_count === 0;
    return `AI-attested coverage through ${compactCoverageDate(scan.covered_through ?? scan.requested_through)}${empty ? " · no relevant alerts selected" : ""}`;
  }
  if (scan.status === "partial") {
    return scan.covered_through
      ? `Partial AI-attested coverage through ${compactCoverageDate(scan.covered_through)}`
      : "Partial scan · no contiguous coverage checkpoint was proven";
  }
  return `${scan.status === "failed" ? "Scan failed" : "Scan abandoned"} · ${scan.covered_through ? `covered through ${compactCoverageDate(scan.covered_through)}` : "no contiguous coverage checkpoint"}`;
}

function compactScanTone(scan: PolicyInboxScan): string {
  if (scan.status === "completed") return "border-emerald-200 bg-emerald-50 text-emerald-900";
  if (scan.status === "running") return "border-info/25 bg-info-soft text-info";
  if (scan.status === "partial") return "border-amber-200 bg-amber-50 text-amber-950";
  return "border-red-200 bg-red-50 text-red-900";
}

function compactWorkPlanTone(summary: PolicyInboxWorkPlanSummary): string {
  if (summary.repair_required_source_count > 0) return "border-red-200 bg-red-50 text-red-900";
  if (summary.running_source_count > 0) return "border-info/25 bg-info-soft text-info";
  if (summary.due_source_count > 0 || summary.never_scanned_source_count > 0) {
    return "border-amber-200 bg-amber-50 text-amber-950";
  }
  if (summary.active_source_count > 0) return "border-emerald-200 bg-emerald-50 text-emerald-900";
  return "border-stone-200 bg-white/80 text-stone-700";
}

function compactWorkPlanCopy(summary: PolicyInboxWorkPlanSummary): string {
  if (summary.registered_source_count === 0) {
    return "No policy-alert source schedule is registered yet.";
  }
  if (summary.active_source_count === 0) {
    return `${summary.registered_source_count} registered · no active coverage target`;
  }
  const needsWork = summary.repair_required_source_count
    + summary.never_scanned_source_count
    + summary.due_source_count;
  const coverage = summary.coverage_percent == null ? "Coverage not established" : `${summary.coverage_percent.toLocaleString()}% current`;
  const details = [
    summary.running_source_count ? `${summary.running_source_count} AI working` : null,
    needsWork ? `${needsWork} need AI attention` : null,
  ].filter(Boolean);
  return `${coverage} across ${summary.active_source_count} active source${summary.active_source_count === 1 ? "" : "s"}${details.length ? ` · ${details.join(" · ")}` : ""}`;
}

function policyPriorityBadgeTone(priority: PolicyDashboardStory["priority_tier"]): BadgeTone {
  if (priority === "urgent") return "danger";
  if (priority === "high") return "warn";
  if (priority === "normal") return "info";
  return "neutral";
}

function policyUpdatedSourceBadge(story: PolicyDashboardStory): string | null {
  // `needs_review` is also true for a never-reviewed story. A prior review
  // revision is what makes this an update rather than an initial arrival.
  if (story.needs_review !== true || Number(story.reviewed_evidence_revision ?? 0) < 1) return null;
  const parsedCount = Number(story.new_source_count ?? 0);
  const newSourceCount = Number.isFinite(parsedCount) ? Math.max(0, Math.trunc(parsedCount)) : 0;
  return newSourceCount > 0 ? `updated +${newSourceCount}` : "updated";
}

export function DashboardPolicyIntelligence({ workspaceId, spec }: WidgetProps) {
  const lens = normalizeDashboardPolicyIntelligenceLens(spec.params);
  const lensKey = dashboardPolicyIntelligenceLensKey(lens);
  const lensQuery = dashboardPolicyIntelligenceSearchParams(lens).toString();
  const lensDescription = describeDashboardPolicyIntelligenceLens(lens);
  const intakeHref = dashboardPolicyIntelligenceIntakeHref(lens);
  const sourceCoverageHref = "/intake#source-coverage-heading";
  const title = typeof spec.title === "string" && spec.title.trim()
    ? spec.title.trim().slice(0, 100)
    : "Policy intelligence";
  const scanQuery = usePolicyInboxScans(workspaceId, 12);
  const workPlanQuery = usePolicyInboxWorkPlan(workspaceId, false);
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["policy-intelligence-summary", workspaceId, ...lensKey],
    retry: (attempt, failure) => failure.message !== "plan_required" && attempt < 2,
    queryFn: async () => {
      const res = await fetch(`/api/v1/workspaces/${encodeURIComponent(workspaceId)}/policy-intelligence?${lensQuery}`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error(res.status === 402 ? "plan_required" : "Failed to load policy intelligence");
      return res.json() as Promise<{
        stories: PolicyDashboardStory[];
        summary: { story_count: number; urgent_count: number; high_count: number; new_count: number };
      }>;
    },
  });
  const stories = data?.stories ?? [];
  if (error?.message === "plan_required") return <Card title={title}>
    <p className="text-sm text-stone-600">Private policy-inbox intelligence requires a paid workspace plan. Explorer includes personal bill tracking and daily in-app watchlist digests.</p>
    <div className="mt-3 flex flex-wrap items-center gap-2">
      <Link to="/personal-setup" className={buttonClass("primary", "sm")}>Set up my personal watchlist</Link>
      <a href="/account" className={buttonClass("secondary", "sm")}>View workspace plans</a>
    </div>
  </Card>;
  if (error) return <Card title={title}><DataUnavailable message="Policy intelligence could not be loaded." retry={refetch} /></Card>;
  const latestScan = scanQuery.data?.scans[0] ?? null;
  const planSummary = workPlanQuery.data?.summary ?? null;
  const planNeedsWork = planSummary
    ? planSummary.repair_required_source_count + planSummary.never_scanned_source_count + planSummary.due_source_count
    : 0;
  return (
    <Card
      title={<>{title}{!!data && <span className="ml-1 font-normal text-stone-500">({data.summary.story_count} matching)</span>}</>}
      actions={<Link to={intakeHref} className="text-xs font-semibold text-action hover:underline">Open intelligence →</Link>}
    >
      <div className="mb-3 text-[11px] leading-relaxed text-stone-600" title={lensDescription}>
        Lens · {lensDescription}
      </div>
      {!workPlanQuery.isLoading && !workPlanQuery.error && planSummary && (
        <Link
          to={sourceCoverageHref}
          className={`mb-2 flex items-center gap-2 rounded-md border px-2.5 py-2 text-[11px] hover:brightness-[0.98] ${compactWorkPlanTone(planSummary)}`}
        >
          <span className={`h-2 w-2 shrink-0 rounded-full ${planSummary.repair_required_source_count > 0 ? "bg-red-500" : planSummary.running_source_count > 0 ? "bg-info animate-pulse" : planNeedsWork > 0 ? "bg-amber-500" : planSummary.active_source_count > 0 ? "bg-emerald-500" : "bg-stone-300"}`} aria-hidden />
          <span className="min-w-0 flex-1 truncate">{compactWorkPlanCopy(planSummary)}</span>
          <span className="shrink-0 font-semibold">Coverage →</span>
        </Link>
      )}
      {!workPlanQuery.isLoading && workPlanQuery.error && <DataUnavailable message="Registered-source coverage could not be loaded." retry={workPlanQuery.refetch} />}
      {!scanQuery.isLoading && scanQuery.error && <DataUnavailable message="Source scan history could not be loaded." retry={scanQuery.refetch} />}
      {!scanQuery.isLoading && !scanQuery.error && latestScan && (
          <Link
            to={sourceCoverageHref}
            className={`mb-3 flex items-center gap-2 rounded-md border px-2.5 py-1.5 text-[10px] hover:brightness-[0.98] ${compactScanTone(latestScan)}`}
          >
            <span className={`h-2 w-2 shrink-0 rounded-full ${latestScan.status === "running" ? "bg-info animate-pulse" : latestScan.status === "completed" ? "bg-emerald-500" : latestScan.status === "partial" ? "bg-amber-500" : "bg-red-500"}`} aria-hidden />
            <span className="min-w-0 flex-1 truncate">Latest run · {compactScanCopy(latestScan)}</span>
          </Link>
      )}
      {isLoading && <SkeletonRows rows={3} label="Loading this intelligence lens…" />}
      {!isLoading && !error && stories.length === 0 && (
        <div className="text-sm text-stone-600">
          {workPlanQuery.error
            ? `No policy intelligence matches this lens (${lensDescription}). Registered-source coverage is currently unavailable.`
            : planSummary?.running_source_count
            ? `No policy intelligence matches this lens (${lensDescription}) yet while your connected authorized AI has source work open.`
            : planNeedsWork > 0
              ? `No policy intelligence matches this lens (${lensDescription}). Ask your connected authorized AI to handle the ${planNeedsWork} registered source${planNeedsWork === 1 ? "" : "s"} that need attention.`
              : planSummary?.active_source_count
                ? `No policy intelligence matches this lens (${lensDescription}), and registered-source coverage is currently up to date.`
                : `No policy intelligence matches this lens (${lensDescription}). Ask your connected authorized AI to register the non-secret labels for the sources you want it to read.`}
        </div>
      )}
      {stories.length > 0 && (
        <ul className="divide-y divide-stone-100">
          {stories.map((story) => (
            <li key={story.id} className="py-2.5 first:pt-0 last:pb-0">
              <Link to={intakeHref} className="block group">
                <div className="flex items-start gap-2">
                  <Badge tone={policyPriorityBadgeTone(story.priority_tier)} className="mt-0.5 shrink-0 capitalize">
                    {story.priority_tier} {story.priority_score}
                  </Badge>
                  {policyUpdatedSourceBadge(story) && (
                    <Badge
                      tone="brand"
                      className="mt-0.5 shrink-0"
                      title="New evidence has arrived since this story was last reviewed"
                    >
                      {policyUpdatedSourceBadge(story)}
                    </Badge>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold text-stone-900 line-clamp-1 group-hover:text-action">{story.title}</div>
                    <div className="text-xs text-stone-600 line-clamp-2 mt-0.5">{story.summary}</div>
                    <div className="text-[11px] text-stone-500 mt-1">{story.source_count} cited source{story.source_count === 1 ? "" : "s"}</div>
                  </div>
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

// ── dashboard.tracked_bills ───────────────────────────────────────────

export function DashboardTrackedBills({ workspaceId }: WidgetProps) {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["tracked-bills-dash", workspaceId],
    queryFn: async () => {
      const res = await fetch(`/api/v1/workspaces/${workspaceId}/tracked-bills`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error("Failed to load tracked bills");
      return res.json();
    },
  });
  if (error) return <DataUnavailable message="Tracked bills could not be loaded." retry={refetch} />;
  const bills: any[] = data?.tracked_bills ?? [];
  const recent = [...bills]
    .sort(
      (a, b) =>
        new Date(b.tracked_since ?? 0).getTime() - new Date(a.tracked_since ?? 0).getTime()
    )
    .slice(0, 8);

  return (
    <Card
      title={<>Tracked bills{bills.length > 0 && <span className="ml-1 font-normal text-stone-500">({bills.length})</span>}</>}
      actions={<CardLink to="/bills">View all</CardLink>}
      className="h-full"
      bodyClassName={recent.length > 0 ? "p-2" : undefined}
    >
      {isLoading && <SkeletonRows rows={3} label="Loading tracked bills…" />}
      {!isLoading && recent.length === 0 && (
        <EmptyState
          icon={<FileText size={20} />}
          title="No bills tracked yet"
          action={<Link to="/bills" className={buttonClass("primary", "sm")}>Find a bill to track</Link>}
        >
          Tracked bills get action alerts, whip counts and coalition context. Or ask your AI client to track one — e.g.{" "}
          <em>“track HR 8573 in this workspace”</em>.
        </EmptyState>
      )}
      {recent.length > 0 && (
        <ul className="space-y-0.5">
          {recent.map((b) => {
            const meta = b.bill_meta ?? {};
            return (
              <li key={b.bill_ref}>
                <Link to={`/bills/${encodeURIComponent(b.bill_ref)}`} className={LIST_ROW}>
                  <div className="flex items-baseline gap-2">
                    <span className="flex-shrink-0 font-mono text-xs font-semibold text-stone-600">
                      {b.bill_ref}
                    </span>
                    <span className="flex-1 truncate font-medium text-stone-900">
                      {meta.title ?? b.project_name ?? b.bill_ref}
                    </span>
                  </div>
                  <div className="mt-0.5 flex flex-wrap items-center gap-2 text-xs text-stone-600">
                    {meta.sponsor_party && (
                      <span className={`inline-block rounded px-1 text-[10px] font-bold ${partyChipClass(meta.sponsor_party)}`}>
                        {meta.sponsor_party.charAt(0)}
                      </span>
                    )}
                    {meta.sponsor_name && <span>{meta.sponsor_name}</span>}
                    {meta.cosponsor_count != null && (
                      <>
                        <span aria-hidden="true">·</span>
                        <span>
                          {meta.cosponsor_count} cosponsor
                          {meta.cosponsor_count === 1 ? "" : "s"}
                        </span>
                      </>
                    )}
                  </div>
                  {meta.latest_action_text && (
                    <div className="mt-0.5 truncate text-[11px] text-stone-500">
                      {meta.latest_action_text.slice(0, 80)}
                    </div>
                  )}
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
}

// ── dashboard.top_relationships ───────────────────────────────────────

const TIER_DOT_DASH: Record<string, string> = {
  hot: "bg-red-500",
  warm: "bg-orange-500",
  cool: "bg-sky-500",
  cold: "bg-stone-400 opacity-60",
};

export function DashboardTopRelationships({ workspaceId }: WidgetProps) {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["members-index-dash", workspaceId],
    queryFn: async () => {
      const res = await fetch(`/api/v1/workspaces/${workspaceId}/members-index`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error("Failed to load relationships");
      return res.json();
    },
  });
  if (error) return <DataUnavailable message="Relationships could not be loaded." retry={refetch} />;
  const members: any[] = data?.members ?? [];
  const top = members
    .filter((m) => (m.touch_count ?? 0) > 0)
    .sort((a, b) => (b.relationship_score ?? 0) - (a.relationship_score ?? 0))
    .slice(0, 8);

  return (
    <Card
      title={<>Top relationships{top.length > 0 && <span className="ml-1 font-normal text-stone-500">({top.length})</span>}</>}
      actions={<CardLink to="/members">All members</CardLink>}
      className="h-full"
      bodyClassName={top.length > 0 ? "p-2" : undefined}
    >
      {isLoading && <SkeletonRows rows={3} label="Loading relationships…" />}
      {!isLoading && top.length === 0 && (
        <EmptyState
          icon={<Users size={20} />}
          title="No member touches recorded yet"
          action={<Link to="/members" className={buttonClass("secondary", "sm")}>Browse members</Link>}
        >
          Relationship strength builds from logged calls, meetings and emails. Ask your AI client to log one — e.g.{" "}
          <em>“log a 30-min call with Sen. Lee yesterday”</em>.
        </EmptyState>
      )}
      {top.length > 0 && (
        <ul className="space-y-0.5">
          {top.map((m) => {
            const partyChar = (m.party || "?").charAt(0);
            return (
              <li key={m.bioguide_id}>
                <Link
                  to={`/members/${encodeURIComponent(m.bioguide_id)}`}
                  className={`${LIST_ROW} flex items-center gap-2.5`}
                >
                  <span
                    aria-hidden="true"
                    className={`h-2 w-2 shrink-0 rounded-full ${
                      TIER_DOT_DASH[m.relationship_tier] ?? "bg-stone-300"
                    }`}
                  />
                  <span className={`inline-block rounded px-1 text-[10px] font-bold ${partyChipClass(m.party)}`}>
                    {partyChar}
                  </span>
                  <span className="flex-1 truncate font-medium text-stone-900">
                    {m.display_name || m.bioguide_id}
                  </span>
                  <span className="font-mono text-xs text-stone-600">
                    {m.state}
                    {m.district ? `-${m.district}` : ""}
                  </span>
                  <span className="text-[11px] tabular-nums text-stone-600">
                    {m.relationship_tier} ({m.relationship_score ?? 0})
                  </span>
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
}

// ── dashboard.feed (AI-rendered panel feed) ───────────────────────────

export function DashboardFeedWidget({ workspaceId }: WidgetProps) {
  return (
    <DashboardFeed workspaceId={workspaceId} heading="AI feed" csrfHeader={getCsrfHeader} />
  );
}

// ── dashboard.activity_stream ─────────────────────────────────────────
//
// Expanded by default with a short window: the dashboard shows recent
// activity at a glance; `params.collapsible: true` restores the folded
// legacy presentation for AI compositions that want it.

export function DashboardActivityStreamWidget({ workspaceId, spec }: WidgetProps) {
  return (
    <WorkspaceActivityStream
      workspaceId={workspaceId}
      collapsible={spec.params?.collapsible === true}
      limit={15}
      heading="Workspace activity"
    />
  );
}

// ── dashboard.scorecards ──────────────────────────────────────────────
//
// At-a-glance scorecard rollups so the user sees "Scorecard X: 23
// champions, 156 opposers, 368 unscored" without navigating in. Pulls
// from /api/v1/workspaces/:wsId/scorecards?with_rollups=true (the
// rollups param is opt-in there so the cheap list-only calls don't
// pay the count fanout).

interface ScorecardListItem {
  id: string;
  name: string;
  description: string | null;
  congress: number | null;
  congresses: number[] | null;
  stance_count: number;
  last_run_at: string | null;
  rollups: { champions: number; opposers: number; cross_pressured: number; unscored: number; total: number } | null;
}

function congressSuffix(n: number): string {
  const last2 = n % 100;
  if (last2 >= 11 && last2 <= 13) return `${n}th`;
  const last = n % 10;
  if (last === 1) return `${n}st`;
  if (last === 2) return `${n}nd`;
  if (last === 3) return `${n}rd`;
  return `${n}th`;
}

export function DashboardScorecards({ workspaceId }: WidgetProps) {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["scorecards", workspaceId, "with_rollups"],
    queryFn: async () => {
      const res = await fetch(`/api/v1/workspaces/${workspaceId}/scorecards?with_rollups=true`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error("Failed to load scorecards");
      return res.json() as Promise<{ scorecards: ScorecardListItem[] }>;
    },
  });
  if (error) return <DataUnavailable message="Scorecards could not be loaded." retry={refetch} />;
  const scorecards = data?.scorecards ?? [];
  // Stable order: most recently run first, then never-run by recency.
  const sorted = [...scorecards].sort((a, b) => {
    const aRun = a.last_run_at ? new Date(a.last_run_at).getTime() : 0;
    const bRun = b.last_run_at ? new Date(b.last_run_at).getTime() : 0;
    return bRun - aRun;
  }).slice(0, 6);

  return (
    <Card
      title={<>Scorecards{scorecards.length > 0 && <span className="ml-1 font-normal text-stone-500">({scorecards.length})</span>}</>}
      actions={<CardLink to="/scorecards">View all</CardLink>}
      className="h-full"
      bodyClassName={sorted.length > 0 ? "p-2" : undefined}
    >
      {isLoading && <SkeletonRows rows={2} label="Loading scorecards…" />}
      {!isLoading && scorecards.length === 0 && (
        <EmptyState
          icon={<BarChart3 size={20} />}
          title="No scorecards yet"
          action={<Link to="/scorecards" className={buttonClass("secondary", "sm")}>Create one</Link>}
        >
          Score members against the bills you care about to find champions, opposers and cross-pressured votes.
        </EmptyState>
      )}
      {sorted.length > 0 && (
        <ul className="grid gap-1 md:grid-cols-2">
          {sorted.map((sc) => {
            const r = sc.rollups;
            const congresses = sc.congresses ?? [];
            const congressLabel = congresses.length === 0
              ? null
              : congresses.length === 1
                ? `${congressSuffix(congresses[0])}`
                : `${congressSuffix(congresses[congresses.length - 1])}–${congressSuffix(congresses[0])}`;
            return (
              <li key={sc.id}>
                <Link to={`/scorecards/${sc.id}`} className={LIST_ROW}>
                  <div className="mb-1 flex items-baseline gap-2">
                    <span className="flex-1 truncate font-semibold text-stone-900">{sc.name}</span>
                    {congressLabel && <Badge tone="neutral">{congressLabel}</Badge>}
                  </div>
                  {r && r.total > 0 ? (
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] tabular-nums">
                      <span className="text-green-700">
                        <strong>{r.champions}</strong> champions
                      </span>
                      <span className="text-amber-700">
                        <strong>{r.cross_pressured}</strong> cross-pressured
                      </span>
                      <span className="text-red-700">
                        <strong>{r.opposers}</strong> opposers
                      </span>
                      <span className="ml-auto text-stone-600">
                        {r.unscored} unscored
                      </span>
                    </div>
                  ) : sc.stance_count === 0 ? (
                    <div className="text-[11px] text-stone-600">
                      No stances yet — add bills to start scoring.
                    </div>
                  ) : !sc.last_run_at ? (
                    <div className="text-[11px] text-amber-700">
                      {sc.stance_count} stance{sc.stance_count === 1 ? "" : "s"} · not yet run
                    </div>
                  ) : (
                    <div className="text-[11px] text-stone-600">
                      {sc.stance_count} stance{sc.stance_count === 1 ? "" : "s"} · 0 members scored
                    </div>
                  )}
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
}

// ── dashboard.widget_grid (legacy ai_widgets grid) ────────────────────
//
// `params.hide_when_empty` (set by the default dashboard spec) hides the
// grid entirely until the assistant deploys a widget, so a fresh
// workspace doesn't open on an empty "No widgets yet" box. Shares the
// ["widgets", workspaceId] query with WidgetGrid.

export function DashboardWidgetGridLegacy({ workspaceId, spec }: WidgetProps) {
  const hideWhenEmpty = spec.params?.hide_when_empty === true;
  const { data: widgets, error, refetch } = useQuery({
    queryKey: ["widgets", workspaceId],
    queryFn: async () => {
      const res = await fetch(`/api/v1/workspaces/${workspaceId}/widgets`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load widgets");
      const json = await res.json();
      return (json.widgets ?? []) as unknown[];
    },
    enabled: hideWhenEmpty,
  });
  if (!hideWhenEmpty) return <WidgetGrid workspaceId={workspaceId} />;
  if (error) return <DataUnavailable message="Widgets could not be loaded." retry={refetch} />;
  if (!widgets || widgets.length === 0) return null;
  return (
    <section>
      <SectionHeading title="Your widgets" description="Live widgets your AI client deployed to this workspace. Drag to rearrange." />
      <WidgetGrid workspaceId={workspaceId} />
    </section>
  );
}
